import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { FiMail, FiPhone, FiUser, FiBriefcase, FiGlobe, FiFileText, FiAward, FiCalendar, FiClock } from 'react-icons/fi';

const EXPERIENCE_YEARS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25'];
const EXPERIENCE_MONTHS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];

export default function Careers() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    position: '',
    dob: '',
    qualification: '',
    portfolio: '',
    lastCompany: '',
    expYear: '',
    expMonth: '',
    preferredLocation: '',
    reference: ''
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
      if (errors.resume) {
        setErrors((prev) => ({
          ...prev,
          resume: ''
        }));
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Mobile number must be exactly 10 digits';
    }

    if (!formData.gender || formData.gender === 'Select..') {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.dob.trim()) {
      newErrors.dob = 'Date of birth is required';
    } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(formData.dob.trim())) {
      newErrors.dob = 'Use format DD/MM/YYYY';
    }

    if (!formData.qualification.trim()) {
      newErrors.qualification = 'Highest qualification is required';
    }

    // Resume Validation
    if (!resumeFile) {
      newErrors.resume = 'Please upload your resume';
    } else {
      const allowedExtensions = /(\.pdf|\.doc|\.docx)$/i;
      if (!allowedExtensions.exec(resumeFile.name)) {
        newErrors.resume = 'Only .pdf, .doc, and .docx formats are allowed';
      }
      // Max size: 2MB (2 * 1024 * 1024 bytes)
      if (resumeFile.size > 2 * 1024 * 1024) {
        newErrors.resume = 'File size must not exceed 2MB';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      let resumeUrl = 'No resume attached';
      if (resumeFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('resume', resumeFile);
        try {
          const res = await fetch('/api/upload-resume', {
            method: 'POST',
            body: formDataUpload
          });
          const data = await res.json();
          if (data.url) resumeUrl = data.url;
        } catch (err) {
          console.error('Failed to upload resume', err);
          resumeUrl = 'Failed to upload resume';
        }
      }

      let body = '';
      if (formData.position) body += `Applying for: ${formData.position}\n`;
      if (formData.preferredLocation) body += `Preferred Location: ${formData.preferredLocation}\n`;
      if (formData.gender) body += `Gender: ${formData.gender}\n`;
      if (formData.dob) body += `DOB: ${formData.dob}\n`;
      if (formData.phone) body += `Mobile: ${formData.phone}\n`;
      if (formData.qualification) body += `Qualification: ${formData.qualification}\n`;
      if (formData.portfolio) body += `Portfolio: ${formData.portfolio}\n`;
      if (formData.lastCompany) body += `Last Company: ${formData.lastCompany}\n`;
      if (formData.expYear || formData.expMonth) body += `Experience: ${formData.expYear || 0} Years, ${formData.expMonth || 0} Months\n`;
      if (formData.reference) body += `\nReference/Comments:\n${formData.reference}\n`;

      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_CAREERS_TEMPLATE_ID,
        {
          title: 'Careers Application - ' + formData.position,
          name: formData.firstName + ' ' + formData.lastName,
          email: formData.email,
          message: body.trim(),
          resume_link: resumeUrl
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
        .then((response) => {
          setSubmitted(true);
          setTimeout(() => setSubmitted(false), 30000);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            gender: '',
            position: '',
            dob: '',
            qualification: '',
            portfolio: '',
            lastCompany: '',
            expYear: '',
            expMonth: '',
            preferredLocation: '',
            reference: ''
          });
          setResumeFile(null);
          const fileInput = document.getElementById('careers-resume');
          if (fileInput) fileInput.value = '';
        })
        .catch((error) => {
          console.error('EmailJS Error:', error);
          alert('Failed to send application. Please try again.');
        });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-hero">
        <h1>Careers</h1>
        <p>Join a network of professionals committed to excellence, integrity, and client success.</p>
      </div>

      <main className="careers-page-main">
        <div className="container">



          {/* Section 2: Form */}
          <div className="careers-form-card">
            <div className="careers-form-header">
              <span className="section-badge">Apply Online</span>
              <h2>Submit Your Application</h2>
              <div className="gold-divider" style={{ margin: '8px auto 24px' }} />
              <p>Provide your details below to submit your resume to our database. Fields marked with <span className="text-danger">*</span> are required.</p>
            </div>

            {submitted && (
              <div className="alert alert-success careers-success-alert">
                <h4>Application Submitted!</h4>
                <p>Your application has been submitted successfully. Our HR team will review your profile and contact you shortly.</p>
                <button className="btn-close" onClick={() => setSubmitted(false)}>Dismiss</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="careers-form">
              <fieldset className="careers-fieldset">
                <legend className="careers-legend">-Apply Here-</legend>

                <div className="form-grid">
                  {/* First Name */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="careers-first-name">
                      First name <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiUser className="input-icon" />
                      <input
                        type="text"
                        id="careers-first-name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="First name"
                        className={`form-input ${errors.firstName ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                  </div>

                  {/* Last Name */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="careers-last-name">
                      Last name <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiUser className="input-icon" />
                      <input
                        type="text"
                        id="careers-last-name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Last name"
                        className={`form-input ${errors.lastName ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="careers-email">
                      Email <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiMail className="input-icon" />
                      <input
                        type="email"
                        id="careers-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@mail.com"
                        className={`form-input ${errors.email ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  {/* Mobile No */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="mobno">
                      Mobile No <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiPhone className="input-icon" />
                      <input
                        type="text"
                        id="mobno"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="10-digit mobile number"
                        className={`form-input ${errors.phone ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>

                  {/* Gender */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="gender">
                      Gender <span className="text-danger">*</span>
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={`form-select ${errors.gender ? 'input-error' : ''}`}
                    >
                      <option value="" disabled hidden style={{ display: 'none' }}>Select..</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                    {errors.gender && <span className="error-message">{errors.gender}</span>}
                  </div>

                  {/* Date of Birth */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="txtdob">
                      Date Of Birth (DD/MM/YYYY) <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiCalendar className="input-icon" />
                      <input
                        type="text"
                        id="txtdob"
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        placeholder="e.g. 15/08/1995"
                        className={`form-input ${errors.dob ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.dob && <span className="error-message">{errors.dob}</span>}
                  </div>

                  {/* Position Applying For */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="careers-position">
                      Position you are applying for
                    </label>
                    <div className="input-with-icon">
                      <FiBriefcase className="input-icon" />
                      <input
                        type="text"
                        id="careers-position"
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        placeholder="e.g. Article Assistant, CA"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Highest Qualification */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="careers-qualification">
                      Highest Qualification <span className="text-danger">*</span>
                    </label>
                    <div className="input-with-icon">
                      <FiAward className="input-icon" />
                      <input
                        type="text"
                        id="careers-qualification"
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        placeholder="e.g. Graduate, CA, CA Inter"
                        className={`form-input ${errors.qualification ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.qualification && <span className="error-message">{errors.qualification}</span>}
                  </div>

                  {/* Portfolio Website */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="careers-portfolio">
                      Portfolio website
                    </label>
                    <div className="input-with-icon">
                      <FiGlobe className="input-icon" />
                      <input
                        type="text"
                        id="careers-portfolio"
                        name="portfolio"
                        value={formData.portfolio}
                        onChange={handleChange}
                        placeholder="e.g. https://myportfolio.com"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Last Company worked for */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="careers-company">
                      Last company you worked for
                    </label>
                    <div className="input-with-icon">
                      <FiBriefcase className="input-icon" />
                      <input
                        type="text"
                        id="careers-company"
                        name="lastCompany"
                        value={formData.lastCompany}
                        onChange={handleChange}
                        placeholder="Previous organization"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Years of Experience - Year */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="expyear">
                      Experience (Years)
                    </label>
                    <div className="input-with-icon">
                      <FiClock className="input-icon" />
                      <select
                        id="expyear"
                        name="expYear"
                        value={formData.expYear}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="" disabled hidden style={{ display: 'none' }}>Select Year</option>
                        {EXPERIENCE_YEARS.map((y) => (
                          <option key={y} value={y}>{y} Years</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Years of Experience - Month */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="expmnth">
                      Experience (Months)
                    </label>
                    <div className="input-with-icon">
                      <FiClock className="input-icon" />
                      <select
                        id="expmnth"
                        name="expMonth"
                        value={formData.expMonth}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="" disabled hidden style={{ display: 'none' }}>Select Month</option>
                        {EXPERIENCE_MONTHS.map((m) => (
                          <option key={m} value={m}>{m} Months</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Preferred Location */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="preferred-location">
                      Preferred Location
                    </label>
                    <div className="input-with-icon">
                      <FiGlobe className="input-icon" />
                      <select
                        id="preferred-location"
                        name="preferredLocation"
                        value={formData.preferredLocation}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="" disabled hidden style={{ display: 'none' }}>Select Location</option>
                        <option value="Shri Vijaya Puram(Port Blair)">Shri Vijaya Puram(Port Blair)</option>
                        <option value="Kolkata">Kolkata</option>
                        <option value="Asansol">Asansol</option>
                      </select>
                    </div>
                  </div>

                  {/* Resume Upload */}
                  <div className="form-group form-group-full">
                    <label className="form-label" htmlFor="careers-resume">
                      Upload Resume <span className="text-danger">*</span> <span className="form-label-instruction">(Max size 2MB, PDF/DOC/DOCX only)</span>
                    </label>
                    <div className="input-file-container">
                      <FiFileText className="input-icon input-file-icon" />
                      <input
                        type="file"
                        id="careers-resume"
                        name="resume"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className={`form-input-file ${errors.resume ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.resume && <span className="error-message">{errors.resume}</span>}
                  </div>

                  {/* References/Comments */}
                  <div className="form-group form-group-full">
                    <label className="form-label" htmlFor="careers-reference">
                      Reference / Comments / Questions
                    </label>
                    <textarea
                      id="careers-reference"
                      name="reference"
                      value={formData.reference}
                      onChange={handleChange}
                      placeholder="Enter any reference details, cover letter statements or questions here..."
                      rows="4"
                      className="form-textarea"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary form-submit-btn">
                    Submit Application
                  </button>
                </div>
              </fieldset>
            </form>
          </div>

        </div>
      </main>
    </div>
  );
}
